import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { SORT_ORDER } from '@catalogues';
import { SortOrder, Study } from '@models';
import { FilterList, StudyFilterService } from '@services';
import { sortByCustomOrder, sortStandard } from '@utils';
import { Subject, takeUntil } from 'rxjs';

type FilterData = Record<string, string>;

@Component({
  selector: 'header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: ['./header-menu.component.scss']
})
export class HeaderMenuComponent implements OnChanges, OnDestroy, OnInit {
  private studyFilterService = inject(StudyFilterService);
  private destroy$ = new Subject<void>();

  @ViewChild('menuTrigger', { static: false }) menuTrigger!: MatMenuTrigger;

  @Input() field = '';
  @Input() showHeaderMenu = false;
  @Input() columnName = '';
  @Input() currentSortColumn: string | null = null;
  @Input() currentSortDirection: 'asc' | 'desc' | null = null;

  @Output() focusOut = new EventEmitter<boolean>();
  @Output() sortRequested = new EventEmitter<'asc' | 'desc'>();
  @Output() menuItemClicked = new EventEmitter<string>();

  activeMenuColumn: string | null = null;
  availableFilters: string[] = [];
  appliedFilters: string[] = [];
  tempSelectedFilters = new Set<string>(); // Temporary selection state

  get filterOptions(): FilterList {
    return this.studyFilterService.getCurrentState().availableOptions;
  }

  get filtersApplied(): FilterList {
    return this.studyFilterService.getCurrentState().appliedFilters;
  }

  ngOnInit() {
    this.studyFilterService.filterState$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateLocalFilterState();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges() {
    this.updateLocalFilterState();
  }

  private updateLocalFilterState() {
    const filters = [...(this.filterOptions[this.field] || [])];
    this.availableFilters = this.sortFilterValues(filters);
    const appliedFiltersSet = new Set([...(this.filtersApplied[this.field] || [])]);
    this.appliedFilters = this.availableFilters.filter((filter) => appliedFiltersSet.has(filter));
    this.tempSelectedFilters = new Set(this.appliedFilters);
  }

  onFilterClear() {
    this.tempSelectedFilters.clear();
    this.studyFilterService.clearFieldFilters(this.field, 'table');
  }

  onFilterApply() {
    const currentFilters = { ...this.studyFilterService.getCurrentState().appliedFilters };
    if (this.tempSelectedFilters.size > 0) {
      currentFilters[this.field] = new Set(this.tempSelectedFilters);
    } else {
      delete currentFilters[this.field];
    }

    this.studyFilterService.setFilters(currentFilters, 'table');

    if (this.menuTrigger) {
      this.menuTrigger.closeMenu();
    }
  }

  onMenuItemClick(direction: 'asc' | 'desc') {
    this.sortRequested.emit(direction);
  }

  onFilterToggle(filter: string, event: any) {
    const isChecked = event.checked;

    if (isChecked) {
      this.tempSelectedFilters.add(filter);
    } else {
      this.tempSelectedFilters.delete(filter);
    }
  }

  isFilterApplied(filter: string): boolean {
    return this.appliedFilters.includes(filter);
  }

  isFilterSelected(filter: string): boolean {
    return this.tempSelectedFilters.has(filter);
  }

  get appliedFiltersCount(): number {
    return this.appliedFilters.length;
  }

  get selectedFiltersCount(): number {
    return this.tempSelectedFilters.size;
  }

  trackByFilter(index: number, filter: string): string {
    return filter;
  }

  private sortFilterValues(filters: string[]): string[] {
    const sortConfig = this.getSortOrderEntry(this.field);

    if (!sortConfig) {
      return [...filters].sort((a, b) => a.localeCompare(b));
    }

    const mockData: FilterData[] = filters.map((filter) => ({
      [this.field]: filter
    }));

    if (sortConfig.alphabetical) {
      const sortedData = sortStandard(
        mockData as unknown as Study[],
        this.field as keyof Study,
        true
      );
      return sortedData.map((item) => (item as unknown as FilterData)[this.field]);
    }

    if (sortConfig.sortingAscending && sortConfig.sortingAscending.length > 0) {
      const sortedData = sortByCustomOrder(
        mockData as unknown as Study[],
        this.field as keyof Study,
        sortConfig.sortingAscending
      );
      return sortedData.map((item) => (item as unknown as FilterData)[this.field]);
    }

    return [...filters].sort((a, b) => a.localeCompare(b));
  }

  private getSortOrderEntry(fieldName: string): SortOrder | undefined {
    return SORT_ORDER.find((order) => order.type.toLowerCase() === fieldName.toLowerCase());
  }
}
